/* Swipe-down-to-close for the mobile menu bottom sheet.

   The sheet itself is Dawn's menu drawer restyled in b3.css; opening and
   closing still go through Dawn's HeaderDrawer, so focus handling, body scroll
   lock and submenu state stay exactly as the theme manages them. This only adds
   the drag: the sheet follows the finger, and letting go either closes it
   (dragged far or flicked) or springs it back. */
(() => {
  const MOBILE = window.matchMedia('(max-width: 989px)');
  const START_THRESHOLD = 6; // px before a touch counts as a drag, so taps still work
  const CLOSE_FRACTION = 0.3; // of the sheet height
  const FLICK_SPEED = 0.5; // px per ms
  const FLICK_MIN = 40; // px, so a tiny fast twitch doesn't close it
  const SETTLE_MS = 380; // matches the sheet transition in b3.css

  function init(drawer) {
    const details = drawer.querySelector('details');
    const summary = details && details.querySelector('summary');
    const sheet = drawer.querySelector('.menu-drawer');
    if (!details || !summary || !sheet) return;

    let startY = 0;
    let startTime = 0;
    let dy = 0;
    let tracking = false;
    let dragging = false;

    const isOpen = () => details.hasAttribute('open') && details.classList.contains('menu-opening');

    // Content scrolled down inside the sheet must scroll back up before a
    // downward swipe is allowed to close it.
    function scrolledAncestor(target) {
      for (let el = target; el && el !== sheet.parentElement; el = el.parentElement) {
        if (el.scrollTop > 0) {
          const overflow = getComputedStyle(el).overflowY;
          if (overflow === 'auto' || overflow === 'scroll') return el;
        }
      }
      return null;
    }

    function setDrag(px) {
      const height = sheet.getBoundingClientRect().height || 1;
      sheet.style.transform = `translateY(${px}px)`;
      drawer.style.setProperty('--b3-sheet-drag', Math.min(1, px / height).toFixed(3));
    }

    function clearInline() {
      sheet.style.transition = '';
      sheet.style.transform = '';
      drawer.style.removeProperty('--b3-sheet-drag');
      drawer.classList.remove('b3-sheet-dragging');
    }

    function settle(transform, then) {
      drawer.classList.remove('b3-sheet-dragging');
      sheet.style.transition = '';
      // Read layout once so the restored stylesheet transition is committed
      // before the move. A requestAnimationFrame here would stall whenever the
      // page isn't painting, leaving the sheet stuck mid-drag.
      void sheet.offsetHeight;
      sheet.style.transform = transform;
      drawer.style.removeProperty('--b3-sheet-drag');
      setTimeout(() => {
        if (then) then();
      }, SETTLE_MS);
    }

    sheet.addEventListener(
      'touchstart',
      (event) => {
        if (!MOBILE.matches || !isOpen() || event.touches.length !== 1) return;
        if (scrolledAncestor(event.target)) return;
        startY = event.touches[0].clientY;
        startTime = event.timeStamp;
        dy = 0;
        tracking = true;
        dragging = false;
      },
      { passive: true }
    );

    sheet.addEventListener(
      'touchmove',
      (event) => {
        if (!tracking) return;
        const delta = event.touches[0].clientY - startY;

        if (!dragging) {
          if (delta < -START_THRESHOLD) {
            tracking = false; // an upward swipe is a scroll, not a close
            return;
          }
          if (delta < START_THRESHOLD) return;
          dragging = true;
          startY = event.touches[0].clientY;
          startTime = event.timeStamp;
          drawer.classList.add('b3-sheet-dragging');
          sheet.style.transition = 'none';
        }

        dy = Math.max(0, event.touches[0].clientY - startY);
        event.preventDefault(); // keep the page behind from scrolling
        setDrag(dy);
      },
      { passive: false }
    );

    function end(event) {
      if (!tracking) return;
      tracking = false;
      if (!dragging) return;
      dragging = false;

      const height = sheet.getBoundingClientRect().height || 1;
      const elapsed = Math.max(1, event.timeStamp - startTime);
      const speed = dy / elapsed;
      const shouldClose = dy > height * CLOSE_FRACTION || (speed > FLICK_SPEED && dy > FLICK_MIN);

      if (shouldClose) {
        // Finish the slide from where the finger left it, then let Dawn close
        // the drawer so its focus and scroll-lock cleanup still run.
        settle('translateY(100%)', clearInline);
        summary.click();
      } else {
        settle('translateY(0)', clearInline);
      }
    }

    sheet.addEventListener('touchend', end);
    sheet.addEventListener('touchcancel', end);
  }

  function boot() {
    document.querySelectorAll('header-drawer').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
